import { apiClient } from '@/api/client';
import {
  AdminBusinessStatusUpdateDTOStatus,
  Business,
  PathsAdminBusinessesGetParametersQueryStatus,
  User,
} from '@/api/models';
import { CanAccess } from '@/components/auth/CanAccess';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { CardBox } from '@/components/ui/CardBox';
import { Icon } from '@/components/ui/Icon';
import { useAuth } from '@/lib/auth';
import { formatTimeAgo } from '@/lib/dateUtils';
import { useNotifications } from '@/lib/notifications';
import { queryKeys } from '@/lib/queryKeys';
import {
  mdiAccount,
  mdiCheckCircle,
  mdiFilter,
  mdiLoading,
  mdiStore,
  mdiTag,
} from '@mdi/js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { Link } from 'react-router-dom';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: string;
  description?: string;
  isLoading?: boolean;
  linkTo?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  description,
  isLoading,
  linkTo,
}) => {
  const content = (
    <CardBox isHoverable={!!linkTo}>
      <div className='flex items-center justify-between'>
        <div>
          <p className='text-sm font-medium text-grey-600'>{title}</p>
          {isLoading ? (
            <div className='flex items-center mt-2'>
              <Icon
                path={mdiLoading}
                size={24}
                className='text-grey-400 animate-spin'
              />
            </div>
          ) : (
            <p className='text-3xl font-bold text-grey-900 mt-2'>{value}</p>
          )}
          {description && (
            <p className='text-sm text-grey-500 mt-2'>{description}</p>
          )}
        </div>
        <div className='p-3 rounded-full bg-grey-50 flex items-center justify-center'>
          <Icon path={icon} size={24} className='text-grey-500' />
        </div>
      </div>
    </CardBox>
  );

  if (linkTo) {
    return <Link to={linkTo}>{content}</Link>;
  }

  return content;
};

interface DashboardStats {
  totalUsers: number;
  totalBusinesses: number;
  activeBusinesses: number;
  pendingBusinesses: number;
  totalCategories: number;
  enabledCategories: number;
  totalFilters: number;
  enabledFilters: number;
}

interface RecentUsersData {
  users: User[];
}

interface PendingBusinessesData {
  businesses: Business[];
}

export const Dashboard: React.FC = () => {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { addNotification } = useNotifications();
  const queryClient = useQueryClient();

  // Fetch dashboard stats
  const { data: stats, isLoading: isStatsLoading } = useQuery({
    queryKey: [queryKeys.ADMIN_DASHBOARD, 'stats'],
    queryFn: async (): Promise<DashboardStats> => {
      const [
        usersRes,
        businessesRes,
        activeRes,
        pendingRes,
        categoriesRes,
        filtersRes,
      ] = await Promise.all([
        apiClient.GET('/admin/users', { params: { query: { limit: 1 } } }),
        apiClient.GET('/admin/businesses', { params: { query: { limit: 1 } } }),
        apiClient.GET('/admin/businesses', {
          params: {
            query: {
              limit: 1,
              status: PathsAdminBusinessesGetParametersQueryStatus.Active,
            },
          },
        }),
        apiClient.GET('/admin/businesses', {
          params: {
            query: {
              limit: 1,
              status: PathsAdminBusinessesGetParametersQueryStatus.Pending,
            },
          },
        }),
        apiClient.GET('/admin/categories', {
          params: { query: { limit: 100 } },
        }),
        apiClient.GET('/admin/filters', { params: { query: { limit: 100 } } }),
      ]);

      const categories = (categoriesRes.data || []) as Array<{
        enabled: boolean;
      }>;
      const filters = (filtersRes.data || []) as Array<{ enabled: boolean }>;

      return {
        totalUsers: parseInt(
          usersRes.response.headers.get('X-Total-Count') || '0',
        ),
        totalBusinesses: parseInt(
          businessesRes.response.headers.get('X-Total-Count') || '0',
        ),
        activeBusinesses: parseInt(
          activeRes.response.headers.get('X-Total-Count') || '0',
        ),
        pendingBusinesses: parseInt(
          pendingRes.response.headers.get('X-Total-Count') || '0',
        ),
        totalCategories: categories.length,
        enabledCategories: categories.filter(c => c.enabled).length,
        totalFilters: filters.length,
        enabledFilters: filters.filter(f => f.enabled).length,
      };
    },
    enabled: isAuthenticated && !isAuthLoading,
  });

  // Fetch recent users
  const { data: recentUsersData, isLoading: isUsersLoading } = useQuery({
    queryKey: [queryKeys.ADMIN_DASHBOARD, 'recentUsers'],
    queryFn: async (): Promise<RecentUsersData> => {
      const response = await apiClient.GET('/admin/users', {
        params: {
          query: { limit: 5, sortBy: 'createdAt', sortOrder: 'desc' },
        },
      });
      return { users: (response.data || []) as User[] };
    },
    enabled: isAuthenticated && !isAuthLoading,
  });

  // Fetch pending businesses
  const { data: pendingData, isLoading: isPendingLoading } = useQuery({
    queryKey: [queryKeys.ADMIN_DASHBOARD, 'pendingBusinesses'],
    queryFn: async (): Promise<PendingBusinessesData> => {
      const response = await apiClient.GET('/admin/businesses', {
        params: {
          query: {
            limit: 5,
            status: PathsAdminBusinessesGetParametersQueryStatus.Pending,
            sortBy: 'createdAt',
            sortOrder: 'desc',
          },
        },
      });
      return { businesses: (response.data || []) as Business[] };
    },
    enabled: isAuthenticated && !isAuthLoading,
  });

  // Status update mutation
  const updateStatusMutation = useMutation({
    mutationFn: async (data: {
      id: number;
      status: AdminBusinessStatusUpdateDTOStatus;
    }) => {
      const response = await apiClient.PUT('/admin/businesses/{id}/status', {
        params: { path: { id: data.id } },
        body: { status: data.status },
      });
      if (response.error) throw new Error('Failed to update status');
      return data;
    },
    onSuccess: data => {
      addNotification({
        type: 'success',
        title: 'Success',
        message: `Business ${data.status === AdminBusinessStatusUpdateDTOStatus.Active ? 'approved' : 'rejected'} successfully`,
      });
      queryClient.invalidateQueries({ queryKey: [queryKeys.ADMIN_DASHBOARD] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.ADMIN_BUSINESSES] });
    },
    onError: () => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to update business status',
      });
    },
  });

  const handleApprove = (business: Business) => {
    if (business.id) {
      updateStatusMutation.mutate({
        id: business.id,
        status: AdminBusinessStatusUpdateDTOStatus.Active,
      });
    }
  };

  const handleReject = (business: Business) => {
    if (business.id) {
      updateStatusMutation.mutate({
        id: business.id,
        status: AdminBusinessStatusUpdateDTOStatus.Inactive,
      });
    }
  };

  return (
    <DashboardLayout>
      <div className='space-y-8'>
        {/* Header */}
        <div>
          <h1 className='text-3xl font-bold text-grey-900'>Dashboard</h1>
          <p className='text-base text-grey-600 mt-2'>
            Welcome to Coffice Admin. Here&apos;s what&apos;s happening with
            your platform.
          </p>
        </div>

        {/* Stats Grid */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
          <CanAccess action='read' subject='User'>
            <StatCard
              title='Total Users'
              value={stats?.totalUsers.toLocaleString() || 0}
              icon={mdiAccount}
              isLoading={isStatsLoading}
              linkTo='/users'
            />
          </CanAccess>

          <CanAccess action='read' subject='Business'>
            <StatCard
              title='Total Businesses'
              value={stats?.totalBusinesses.toLocaleString() || 0}
              icon={mdiStore}
              isLoading={isStatsLoading}
              linkTo='/businesses'
            />
          </CanAccess>

          <CanAccess action='read' subject='Business'>
            <StatCard
              title='Active Businesses'
              value={stats?.activeBusinesses.toLocaleString() || 0}
              icon={mdiStore}
              isLoading={isStatsLoading}
              linkTo='/businesses'
            />
          </CanAccess>

          <CanAccess action='read' subject='Business'>
            <StatCard
              title='Pending Approvals'
              value={stats?.pendingBusinesses.toLocaleString() || 0}
              icon={mdiCheckCircle}
              description={
                stats?.pendingBusinesses
                  ? `${stats.pendingBusinesses} require attention`
                  : undefined
              }
              isLoading={isStatsLoading}
              linkTo='/businesses'
            />
          </CanAccess>
        </div>

        {/* Quick Actions */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
          <CanAccess action='read' subject='User'>
            <CardBox>
              <div className='flex items-center justify-between mb-4'>
                <h3 className='text-lg font-semibold text-grey-900'>
                  Recent Users
                </h3>
                <Link
                  to='/users'
                  className='text-sm text-coffice-blue hover:text-coffice-blue/80'
                >
                  View all
                </Link>
              </div>
              <div className='space-y-3'>
                {isUsersLoading ? (
                  <div className='flex items-center justify-center py-8'>
                    <Icon
                      path={mdiLoading}
                      size={24}
                      className='text-grey-400 animate-spin'
                    />
                  </div>
                ) : recentUsersData?.users.length === 0 ? (
                  <p className='text-grey-500 text-center py-8'>No users yet</p>
                ) : (
                  recentUsersData?.users.map(user => (
                    <Link
                      key={user.id}
                      to={`/users/${user.id}`}
                      className='flex items-center justify-between py-3 hover:bg-grey-50 -mx-2 px-2 rounded-lg'
                    >
                      <div>
                        <p className='font-medium text-grey-900'>
                          {user.firstName} {user.lastName}
                        </p>
                        <p className='text-sm text-grey-500'>ID: {user.id}</p>
                      </div>
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${
                          user.enabled
                            ? 'bg-coffice-green/30 text-grey-900'
                            : 'bg-grey-100 text-grey-600'
                        }`}
                      >
                        {user.enabled ? 'Active' : 'Disabled'}
                      </span>
                    </Link>
                  ))
                )}
              </div>
            </CardBox>
          </CanAccess>

          <CanAccess action='read' subject='Business'>
            <CardBox>
              <div className='flex items-center justify-between mb-4'>
                <h3 className='text-lg font-semibold text-grey-900'>
                  Pending Business Approvals
                </h3>
                <Link
                  to='/businesses'
                  className='text-sm text-coffice-blue hover:text-coffice-blue/80'
                >
                  View all
                </Link>
              </div>
              <div className='space-y-3'>
                {isPendingLoading ? (
                  <div className='flex items-center justify-center py-8'>
                    <Icon
                      path={mdiLoading}
                      size={24}
                      className='text-grey-400 animate-spin'
                    />
                  </div>
                ) : pendingData?.businesses.length === 0 ? (
                  <p className='text-grey-500 text-center py-8'>
                    No pending approvals
                  </p>
                ) : (
                  pendingData?.businesses.map(business => (
                    <div
                      key={business.id}
                      className='flex items-center justify-between py-3'
                    >
                      <Link
                        to={`/businesses/${business.id}`}
                        className='flex-1'
                      >
                        <p className='font-medium text-grey-900'>
                          {business.name}
                        </p>
                        <p className='text-sm text-grey-600'>
                          {business.city} &bull;{' '}
                          {formatTimeAgo(business.createdAt || '')}
                        </p>
                      </Link>
                      <div className='flex gap-2'>
                        <button
                          onClick={() => handleApprove(business)}
                          disabled={updateStatusMutation.isPending}
                          className='px-3 py-1 text-sm text-coffice-green hover:bg-coffice-green/10 rounded-lg disabled:opacity-50'
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleReject(business)}
                          disabled={updateStatusMutation.isPending}
                          className='px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50'
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardBox>
          </CanAccess>
        </div>

        {/* Categories and Filters Overview */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
          <CanAccess action='read' subject='Category'>
            <CardBox>
              <div className='flex items-center justify-between mb-4'>
                <div className='flex items-center gap-3'>
                  <div className='p-2 rounded-full bg-grey-50'>
                    <Icon path={mdiTag} size={20} className='text-grey-500' />
                  </div>
                  <h3 className='text-lg font-semibold text-grey-900'>
                    Categories
                  </h3>
                </div>
                <Link
                  to='/categories'
                  className='text-sm text-coffice-blue hover:text-coffice-blue/80'
                >
                  Manage
                </Link>
              </div>
              {isStatsLoading ? (
                <div className='flex items-center justify-center py-4'>
                  <Icon
                    path={mdiLoading}
                    size={24}
                    className='text-grey-400 animate-spin'
                  />
                </div>
              ) : (
                <div className='flex items-center gap-8'>
                  <div>
                    <p className='text-3xl font-bold text-grey-900'>
                      {stats?.enabledCategories || 0}
                    </p>
                    <p className='text-sm text-grey-500'>Enabled</p>
                  </div>
                  <div className='text-grey-300'>/</div>
                  <div>
                    <p className='text-3xl font-bold text-grey-400'>
                      {stats?.totalCategories || 0}
                    </p>
                    <p className='text-sm text-grey-500'>Total</p>
                  </div>
                </div>
              )}
            </CardBox>
          </CanAccess>

          <CanAccess action='read' subject='Filter'>
            <CardBox>
              <div className='flex items-center justify-between mb-4'>
                <div className='flex items-center gap-3'>
                  <div className='p-2 rounded-full bg-grey-50'>
                    <Icon
                      path={mdiFilter}
                      size={20}
                      className='text-grey-500'
                    />
                  </div>
                  <h3 className='text-lg font-semibold text-grey-900'>
                    Filters
                  </h3>
                </div>
                <Link
                  to='/filters'
                  className='text-sm text-coffice-blue hover:text-coffice-blue/80'
                >
                  Manage
                </Link>
              </div>
              {isStatsLoading ? (
                <div className='flex items-center justify-center py-4'>
                  <Icon
                    path={mdiLoading}
                    size={24}
                    className='text-grey-400 animate-spin'
                  />
                </div>
              ) : (
                <div className='flex items-center gap-8'>
                  <div>
                    <p className='text-3xl font-bold text-grey-900'>
                      {stats?.enabledFilters || 0}
                    </p>
                    <p className='text-sm text-grey-500'>Enabled</p>
                  </div>
                  <div className='text-grey-300'>/</div>
                  <div>
                    <p className='text-3xl font-bold text-grey-400'>
                      {stats?.totalFilters || 0}
                    </p>
                    <p className='text-sm text-grey-500'>Total</p>
                  </div>
                </div>
              )}
            </CardBox>
          </CanAccess>
        </div>
      </div>
    </DashboardLayout>
  );
};
